import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  GripVertical,
  Info,
  Plus,
  Save,
  Trash2,
  Wand2,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { ghl } from "../../enums";
import env from "../../config";
import { cx, toEmbedUrl } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Input, { Label, Select } from "../../components/ui/Input";
import FeatureSelect from "../../components/ui/FeatureSelect";
import RichTextEditor from "../../components/ui/RichTextEditor";
import Skeleton from "../../components/ui/Skeleton";
import Tabs from "../../components/ui/Tabs";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import UsageEditor, { usageProblems, withKeys } from "./UsageEditor";
import PlansEditor, { planProblems } from "./PlansEditor";

// Only these keys are sent to the API — anything else on the document
// (timestamps, execution counters, __v) is left for the backend to own.
const EDITABLE = [
  "appName",
  "appId",
  "shortDescription",
  "description",
  "appImage",
  "appType",
  "whoCanInstall",
  "sso",
  "funKey",
  "clientId",
  "clientSecret",
  "conversationProviderId",
  "featuresInclude",
  "formFields",
  "copy_buttons",
  "installMessage",
  "getStarted",
  "usage",
  "plans",
];

const BLANK = {
  appName: "",
  appId: "",
  shortDescription: "",
  description: "",
  appImage: "",
  appType: ghl.AppTypes.SUB_ACCOUNT,
  whoCanInstall: ghl.WhoCanInstall.ANYONE,
  sso: "",
  funKey: "",
  clientId: "",
  clientSecret: "",
  conversationProviderId: "",
  featuresInclude: [],
  formFields: [],
  copy_buttons: [],
  installMessage: "",
  getStarted: { video: "", content: "" },
  // The app page's How to use tab — see UsageEditor.
  usage: { intro: "", sections: [] },
  // Paid plans: marketplace plan ID → name and price. See PlansEditor.
  plans: [],
};

// "Stripe Secret Key" -> "stripe_secret_key". Credential values are stored on
// AppInstalls under this key, so it has to be stable and code-friendly.
const slugify = (label = "") =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// A dropdown's options are edited as text, one per line: "value", or
// "Label | value" when what customers see should differ from what is stored.
const optionsToText = (options = []) =>
  (options || []).map((o) => (o.label && o.label !== o.value ? `${o.label} | ${o.value}` : o.value)).join("\n");

const textToOptions = (text = "") =>
  String(text)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const bar = line.lastIndexOf("|");
      if (bar < 0) return { label: line, value: line };
      const label = line.slice(0, bar).trim();
      const value = line.slice(bar + 1).trim();
      return { label: label || value, value };
    })
    .filter((o) => o.value);

const Field = ({ label, hint, required, children }) => (
  <div>
    <Label hint={hint} required={required}>
      {label}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const AppForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { apps } = useAppServices();
  const toast = useToast();
  const confirm = useConfirm();

  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("general");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isEdit) return undefined;
    let alive = true;

    (async () => {
      const { response, error } = await apps.Filter(`_id=${id}`);
      if (!alive) return;

      if (error || !response) {
        toast(error?.message || "That app could not be found", "error");
        navigate("/apps", { replace: true });
        return;
      }

      // Merge onto BLANK so a document saved before a field existed still
      // gives every input a defined value (no controlled/uncontrolled warning).
      setForm({
        ...BLANK,
        ...response,
        featuresInclude: Array.isArray(response.featuresInclude)
          ? response.featuresInclude
          : [],
        formFields: Array.isArray(response.formFields)
          ? response.formFields.map((f) => ({ ...f, type: f.type || "text", _optionsText: optionsToText(f.options) }))
          : [],
        copy_buttons: Array.isArray(response.copy_buttons) ? response.copy_buttons : [],
        getStarted: { ...BLANK.getStarted, ...(response.getStarted || {}) },
        usage: withKeys(response.usage),
        plans: Array.isArray(response.plans) ? response.plans.map((p) => ({ ...p, amount: p.amount ?? "", trialDays: p.trialDays || "" })) : [],
      });
      setLoading(false);
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }, []);

  const setGetStarted = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, getStarted: { ...prev.getStarted, [key]: value } }));
  }, []);

  /* ---- form fields (repeatable) ---- */

  const addField = () =>
    setForm((prev) => ({
      ...prev,
      formFields: [...prev.formFields, { label: "", key: "", short_description: "", type: "text", _optionsText: "" }],
    }));

  const updateField = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      formFields: prev.formFields.map((f, i) => (i === index ? { ...f, [key]: value } : f)),
    }));

  // Auto-fill the key from the label, but only while the key is untouched —
  // renaming a live field would orphan every stored credential.
  const onFieldLabelChange = (index, value) =>
    setForm((prev) => ({
      ...prev,
      formFields: prev.formFields.map((f, i) => {
        if (i !== index) return f;
        const autoKey = !f.key || f.key === slugify(f.label);
        return { ...f, label: value, ...(autoKey && { key: slugify(value) }) };
      }),
    }));

  const removeField = (index) =>
    setForm((prev) => ({
      ...prev,
      formFields: prev.formFields.filter((_, i) => i !== index),
    }));

  const moveField = (index, delta) =>
    setForm((prev) => {
      const next = [...prev.formFields];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, formFields: next };
    });

  /* ---- copy buttons (repeatable) ---- */

  const addCopyButton = () =>
    setForm((prev) => ({
      ...prev,
      copy_buttons: [
        ...prev.copy_buttons,
        { title: "", description: "", copy_url: "" },
      ],
    }));

  const updateCopyButton = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      copy_buttons: prev.copy_buttons.map((b, i) =>
        i === index ? { ...b, [key]: value } : b,
      ),
    }));

  const removeCopyButton = (index) =>
    setForm((prev) => ({
      ...prev,
      copy_buttons: prev.copy_buttons.filter((_, i) => i !== index),
    }));

  const moveCopyButton = (index, delta) =>
    setForm((prev) => {
      const next = [...prev.copy_buttons];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, copy_buttons: next };
    });

  /* ---- save ---- */

  const validate = () => {
    const next = {};
    const usageIssues = usageProblems(form.usage);
    if (usageIssues.length) next.usage = usageIssues;
    const planIssues = planProblems(form.plans);
    if (planIssues.length) next.plans = planIssues;
    if (!form.appName.trim()) next.appName = "An app name is required.";
    if (!form.appId.trim()) next.appId = "The marketplace app ID is required.";

    const keys = form.formFields.map((f) => f.key.trim()).filter(Boolean);
    if (form.formFields.some((f) => !f.key.trim()))
      next.formFields = "Every credential field needs a key.";
    else if (new Set(keys).size !== keys.length)
      next.formFields = "Credential keys must be unique.";
    else if (form.formFields.some((f) => f.type === "select" && textToOptions(f._optionsText).length === 0))
      next.formFields = "Every dropdown needs at least one option.";

    // A button with no title has nothing to click, and one with no URL copies
    // an empty string — both are silently useless on the public page.
    if (form.copy_buttons.some((b) => !b.title.trim() || !b.copy_url.trim()))
      next.copy_buttons = "Every copy button needs a title and a value to copy.";

    setErrors(next);
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const found = validate();
    if (Object.keys(found).length > 0) {
      if (found.appName || found.appId) setTab("general");
      else if (found.formFields) setTab("fields");
      else if (found.copy_buttons) setTab("copy");
      else if (found.usage) setTab("usage");
      else if (found.plans) setTab("plans");
      toast("Fix the highlighted fields before saving", "error");
      return;
    }

    const payload = Object.fromEntries(EDITABLE.map((k) => [k, form[k]]));
    payload.formFields = payload.formFields.map(({ _optionsText, ...f }) => ({
      ...f,
      key: f.key.trim(),
      label: f.label.trim(),
      type: f.type === "select" ? "select" : "text",
      options: f.type === "select" ? textToOptions(_optionsText) : [],
    }));
    payload.copy_buttons = payload.copy_buttons.map((b) => ({
      ...b,
      title: b.title.trim(),
      description: (b.description || "").trim(),
      copy_url: b.copy_url.trim(),
    }));

    payload.usage = {
      intro: form.usage.intro.trim(),
      sections: form.usage.sections.map(({ _key, ...section }) => ({
        ...section,
        where: (section.where || []).filter((x) => x.trim()),
        steps: (section.steps || []).filter((x) => x.trim()),
        tips: (section.tips || []).filter((x) => x.trim()),
        actions: (section.actions || []).filter((a) => a.name?.trim()),
      })),
    };

    // Empty rows are dropped; a blank price means a free plan.
    payload.plans = form.plans
      .filter((p) => String(p.planId || "").trim() || String(p.name || "").trim())
      .map((p) => ({
        planId: String(p.planId || "").trim(),
        name: String(p.name || "").trim(),
        amount: p.amount === "" || p.amount === null || p.amount === undefined ? 0 : Number(p.amount),
        currency: p.currency || "USD",
        interval: p.interval || "month",
        trialDays: p.trialDays === "" || p.trialDays === null || p.trialDays === undefined ? 0 : Number(p.trialDays),
      }));

    setSaving(true);
    const { response, error } = isEdit
      ? await apps.Update({ _id: id, ...payload })
      : await apps.Create(payload);
    setSaving(false);

    if (error) {
      toast(error?.message || "Could not save the app", "error");
      return;
    }

    toast(isEdit ? "App updated" : "App created");
    if (!isEdit && response?._id) navigate(`/apps/${response._id}/edit`, { replace: true });
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: `Delete ${form.appName || "this app"}?`,
      message:
        "This permanently removes the app and everything configured on it. Existing installs will stop resolving. This cannot be undone.",
      confirmLabel: "Delete app",
      tone: "danger",
    });
    if (!ok) return;

    const { error } = await apps.Delete(id);
    if (error) {
      toast(error?.message || "Could not delete the app", "error");
      return;
    }
    toast("App deleted");
    navigate("/apps", { replace: true });
  };

  const videoPreview = useMemo(
    () => toEmbedUrl(form.getStarted.video || ""),
    [form.getStarted.video],
  );

  const tabs = [
    { id: "general", label: "General" },
    { id: "oauth", label: "OAuth & provider" },
    { id: "fields", label: "Credential fields", badge: form.formFields.length },
    { id: "copy", label: "Copy buttons", badge: form.copy_buttons.length },
    { id: "onboarding", label: "Get started" },
    { id: "usage", label: "How to use", badge: form.usage.sections.length },
    { id: "plans", label: "Plans", badge: form.plans.length },
  ];

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" rounded="rounded-2xl" />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/apps"
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-500 hover:text-ink-900"
        >
          <ArrowLeft size={14} />
          Apps
        </Link>

        <div className="ml-auto flex items-center gap-2">
          {isEdit && (
            <Button
              type="button"
              variant="ghost"
              onClick={onDelete}
              icon={<Trash2 size={14} />}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            >
              Delete
            </Button>
          )}
          <Button type="submit" variant="gradient" loading={saving} icon={!saving && <Save size={14} />}>
            {isEdit ? "Save changes" : "Create app"}
          </Button>
        </div>
      </div>

      {/* Identity preview */}
      <div className="flex items-center gap-4">
        <AppIcon app={form} size="lg" />
        <div className="min-w-0">
          <h2 className="truncate text-h1 text-ink-900">
            {form.appName || (isEdit ? "Untitled app" : "New app")}
          </h2>
          <p className="truncate font-mono text-xs text-ink-400">
            {form.appId || "no app id yet"}
          </p>
        </div>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {/* ---------------- General ---------------- */}
      {tab === "general" && (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card title="Identity" className="lg:col-span-2">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="App name" required>
                <Input
                  value={form.appName}
                  onChange={(e) => set("appName", e.target.value)}
                  placeholder="Smart Links"
                  invalid={Boolean(errors.appName)}
                />
                {errors.appName && (
                  <p className="mt-1.5 text-xs text-rose-600">{errors.appName}</p>
                )}
              </Field>

              <Field label="App ID" hint="from the marketplace listing" required>
                <Input
                  value={form.appId}
                  onChange={(e) => set("appId", e.target.value)}
                  placeholder="6a328e319fb1a492f109478e"
                  className="font-mono text-[13px]"
                  invalid={Boolean(errors.appId)}
                />
                {errors.appId && <p className="mt-1.5 text-xs text-rose-600">{errors.appId}</p>}
              </Field>

              <Field label="Short description" hint="shown on cards">
                <Input
                  value={form.shortDescription}
                  onChange={(e) => set("shortDescription", e.target.value)}
                  placeholder="Shorten and track links inside your CRM."
                  maxLength={160}
                />
              </Field>

              <Field label="App image URL">
                <Input
                  value={form.appImage}
                  onChange={(e) => set("appImage", e.target.value)}
                  placeholder="https://cdn.example.com/icon.png"
                />
              </Field>

              <div className="sm:col-span-2">
                <Field label="Full description">
                  <RichTextEditor
                    value={form.description}
                    onChange={(html) => set("description", html)}
                    placeholder="What the app does, who it's for, and what it connects to."
                    minHeight="12rem"
                  />
                </Field>
              </div>
            </div>
          </Card>

          <Card title="Availability">
            <div className="space-y-5">
              <Field label="App type">
                <Select value={form.appType} onChange={(e) => set("appType", e.target.value)}>
                  {Object.values(ghl.AppTypes).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Who can install">
                <Select
                  value={form.whoCanInstall}
                  onChange={(e) => set("whoCanInstall", e.target.value)}
                >
                  {Object.values(ghl.WhoCanInstall).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </Field>

              <div className="flex items-start gap-2.5 rounded-lg bg-ink-50 px-3.5 py-3">
                <Info size={14} className="mt-0.5 shrink-0 text-ink-400" />
                <p className="text-xs leading-relaxed text-ink-500">
                  A <strong>Sub Account</strong> app installable by <strong>Anyone</strong> makes
                  the backend fan out across every location on install.
                </p>
              </div>
            </div>
          </Card>

          <Card
            title="Features included"
            subtitle="What this app plugs into. Shown on the app's public page."
          >
            <FeatureSelect
              value={form.featuresInclude}
              onChange={(next) => set("featuresInclude", next)}
            />
          </Card>
        </div>
      )}

      {/* ---------------- OAuth ---------------- */}
      {tab === "oauth" && (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card title="OAuth credentials" subtitle="From the marketplace app settings.">
            <div className="space-y-5">
              <Field label="Client ID">
                <Input
                  value={form.clientId}
                  onChange={(e) => set("clientId", e.target.value)}
                  className="font-mono text-[13px]"
                  autoComplete="off"
                />
              </Field>
              <Field label="Client secret">
                <Input
                  type="password"
                  value={form.clientSecret}
                  onChange={(e) => set("clientSecret", e.target.value)}
                  className="font-mono text-[13px]"
                  autoComplete="new-password"
                />
              </Field>
              <Field label="SSO key">
                <Input
                  type="password"
                  value={form.sso}
                  onChange={(e) => set("sso", e.target.value)}
                  className="font-mono text-[13px]"
                  autoComplete="new-password"
                />
              </Field>
            </div>
          </Card>

          <Card title="Provider settings">
            <div className="space-y-5">
              <Field label="Conversation provider ID">
                <Input
                  value={form.conversationProviderId}
                  onChange={(e) => set("conversationProviderId", e.target.value)}
                  className="font-mono text-[13px]"
                />
              </Field>
              <Field label="Function key" hint="internal">
                <Input
                  value={form.funKey}
                  onChange={(e) => set("funKey", e.target.value)}
                  className="font-mono text-[13px]"
                />
              </Field>
            </div>
          </Card>
        </div>
      )}

      {/* ---------------- Credential fields ---------------- */}
      {tab === "fields" && (
        <Card
          title="Credential fields"
          subtitle="What each install is asked for. Values are stored per install, never here."
          action={
            <Button type="button" size="sm" variant="secondary" onClick={addField} icon={<Plus size={14} />}>
              Add field
            </Button>
          }
        >
          {errors.formFields && (
            <p className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[13px] text-rose-700">
              {errors.formFields}
            </p>
          )}

          {form.formFields.length === 0 ? (
            <div className="rounded-xl border border-dashed border-ink-200 px-6 py-10 text-center">
              <p className="text-sm font-medium text-ink-700">No credential fields</p>
              <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-ink-500">
                Add one for each value the app needs from the person installing it — an API key,
                an account ID, a webhook secret.
              </p>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={addField}
                className="mt-4"
                icon={<Plus size={14} />}
              >
                Add field
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {form.formFields.map((field, index) => (
                <div
                  key={field._id || index}
                  className="rounded-xl border border-ink-200 bg-ink-50/50 p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center gap-0.5 pt-1.5">
                      <button
                        type="button"
                        onClick={() => moveField(index, -1)}
                        disabled={index === 0}
                        className="text-ink-300 hover:text-ink-600 disabled:opacity-30"
                        aria-label="Move up"
                      >
                        <GripVertical size={14} />
                      </button>
                      <span className="text-[10px] font-semibold text-ink-400">{index + 1}</span>
                    </div>

                    <div className="grid flex-1 gap-4 sm:grid-cols-2">
                      <Field label="Label">
                        <Input
                          value={field.label}
                          onChange={(e) => onFieldLabelChange(index, e.target.value)}
                          placeholder="API key"
                        />
                      </Field>
                      <Field label="Key" hint="stored under this name">
                        <Input
                          value={field.key}
                          onChange={(e) => updateField(index, "key", slugify(e.target.value))}
                          placeholder="api_key"
                          className="font-mono text-[13px]"
                          invalid={Boolean(errors.formFields) && !field.key.trim()}
                        />
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Help text" hint="optional">
                          <Input
                            value={field.short_description || ""}
                            onChange={(e) =>
                              updateField(index, "short_description", e.target.value)
                            }
                            placeholder="Found under Settings → API in your dashboard."
                          />
                        </Field>
                      </div>
                      <Field label="Type" hint="what customers fill in">
                        <Select
                          value={field.type || "text"}
                          onChange={(e) => updateField(index, "type", e.target.value)}
                        >
                          <option value="text">Text box</option>
                          <option value="select">Dropdown</option>
                        </Select>
                      </Field>
                      {field.type === "select" && (
                        <div className="sm:col-span-2">
                          <Field label="Options" hint="one per line — value, or Label | value">
                            <textarea
                              value={field._optionsText || ""}
                              onChange={(e) => updateField(index, "_optionsText", e.target.value)}
                              rows={Math.min(Math.max((field._optionsText || "").split("\n").length, 4), 12)}
                              placeholder={"gpt-6-luna — fast and low cost | gpt-6-luna\ngpt-5.4-mini | gpt-5.4-mini"}
                              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-[12.5px] leading-6 text-ink-900 shadow-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                            />
                          </Field>
                          <p className="mt-1.5 text-[12px] leading-relaxed text-ink-500">
                            Customers pick one of these on the Credentials tab. An optional field (help text starting with
                            “Optional”) also offers “Recommended default”, which saves nothing.
                          </p>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeField(index)}
                      className="mt-1.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600"
                      aria-label="Remove field"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ---------------- Copy buttons ---------------- */}
      {tab === "copy" && (
        <Card
          title="Copy buttons"
          subtitle="One-click values the installer pastes into your CRM — shown on the app's Get started page."
          action={
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={addCopyButton}
              icon={<Plus size={14} />}
            >
              Add button
            </Button>
          }
        >
          <div className="mb-4 flex items-start gap-2.5 rounded-lg bg-ink-50 px-3.5 py-3">
            <Info size={14} className="mt-0.5 shrink-0 text-ink-400" />
            <p className="text-xs leading-relaxed text-ink-500">
              Values are copied verbatim, except{" "}
              <code className="font-mono">{"{{locationId}}"}</code>,{" "}
              <code className="font-mono">{"{{companyId}}"}</code> and{" "}
              <code className="font-mono">{"{{appId}}"}</code>, which are replaced with the
              viewing account&rsquo;s own values. Use them for per-install endpoints such as an
              inbound webhook.
            </p>
          </div>

          {errors.copy_buttons && (
            <p className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[13px] text-rose-700">
              {errors.copy_buttons}
            </p>
          )}

          {form.copy_buttons.length === 0 ? (
            <div className="rounded-xl border border-dashed border-ink-200 px-6 py-10 text-center">
              <p className="text-sm font-medium text-ink-700">No copy buttons</p>
              <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-ink-500">
                Add one for each value someone has to paste into your CRM during setup — an
                inbound webhook URL, a provider endpoint, a callback address.
              </p>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={addCopyButton}
                className="mt-4"
                icon={<Plus size={14} />}
              >
                Add button
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {form.copy_buttons.map((button, index) => (
                <div
                  key={button._id || index}
                  className="rounded-xl border border-ink-200 bg-ink-50/50 p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col items-center gap-0.5 pt-1.5">
                      <button
                        type="button"
                        onClick={() => moveCopyButton(index, -1)}
                        disabled={index === 0}
                        className="text-ink-300 hover:text-ink-600 disabled:opacity-30"
                        aria-label="Move up"
                      >
                        <GripVertical size={14} />
                      </button>
                      <span className="text-[10px] font-semibold text-ink-400">
                        {index + 1}
                      </span>
                    </div>

                    <div className="grid flex-1 gap-4 sm:grid-cols-2">
                      <Field label="Button title">
                        <Input
                          value={button.title}
                          onChange={(e) => updateCopyButton(index, "title", e.target.value)}
                          placeholder="Inbound webhook URL"
                          invalid={Boolean(errors.copy_buttons) && !button.title.trim()}
                        />
                      </Field>
                      <Field label="Tooltip" hint="optional">
                        <Input
                          value={button.description || ""}
                          onChange={(e) =>
                            updateCopyButton(index, "description", e.target.value)
                          }
                          placeholder="Paste this into the provider's webhook settings"
                        />
                      </Field>
                      <div className="sm:col-span-2">
                        <Field
                          label="Value to copy"
                          hint="supports {{locationId}}, {{companyId}}, {{appId}}"
                        >
                          <Input
                            value={button.copy_url}
                            onChange={(e) =>
                              updateCopyButton(index, "copy_url", e.target.value)
                            }
                            placeholder="https://api.example.com/webhook/message_events/{{locationId}}/{{appId}}"
                            className="font-mono text-[13px]"
                            invalid={
                              Boolean(errors.copy_buttons) && !button.copy_url.trim()
                            }
                          />
                        </Field>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeCopyButton(index)}
                      className="mt-1.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600"
                      aria-label="Remove button"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ---------------- How to use ---------------- */}
      {tab === "usage" && (
        <div className="space-y-4">
          {errors.usage?.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[13px] text-rose-700">
              <p className="font-medium">Fix these before saving:</p>
              <ul className="mt-1 list-disc pl-5">
                {errors.usage.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          <UsageEditor usage={form.usage} onChange={(usage) => set("usage", usage)} />
        </div>
      )}

      {/* ---------------- Plans ---------------- */}
      {tab === "plans" && (
        <div className="space-y-4">
          {errors.plans?.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[13px] text-rose-700">
              <p className="font-medium">Fix these before saving:</p>
              <ul className="mt-1 list-disc pl-5">
                {errors.plans.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          <PlansEditor appId={isEdit ? form.appId : ""} plans={form.plans} onChange={(plans) => set("plans", plans)} />
        </div>
      )}

      {/* ---------------- Get started ---------------- */}
      {tab === "onboarding" && (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="space-y-5">
            <Card
              title="Install page"
              subtitle="The confirmation screen shown the moment the app is installed."
            >
              <RichTextEditor
                value={form.installMessage}
                onChange={(html) => set("installMessage", html)}
                placeholder="Tell the installer exactly what to do next — use a numbered list for the steps."
                minHeight="13rem"
              />
            </Card>

            <Card title="Get started content" subtitle="Shown on the app's public Get started page.">
              <div className="space-y-5">
                <Field label="Walkthrough video" hint="YouTube, Vimeo or Loom">
                  <Input
                    value={form.getStarted.video}
                    onChange={(e) => setGetStarted("video", e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </Field>

                <Field label="Setup instructions">
                  <RichTextEditor
                    value={form.getStarted.content}
                    onChange={(html) => setGetStarted("content", html)}
                    placeholder="Walk the installer through connecting the app…"
                    minHeight="22rem"
                  />
                </Field>

                <div className="flex items-start gap-2.5 rounded-lg bg-ink-50 px-3.5 py-3">
                  <Wand2 size={14} className="mt-0.5 shrink-0 text-ink-400" />
                  <p className="text-xs leading-relaxed text-ink-500">
                    The editor shows this the way the public page will. Switch to{" "}
                    <strong>HTML</strong> in the toolbar to edit the markup directly — it is
                    sanitized before the public page renders it.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          <aside className="space-y-5">
            <Card title="Video preview">
              {videoPreview ? (
                <div className="aspect-video overflow-hidden rounded-xl bg-ink-900">
                  <iframe
                    src={videoPreview}
                    title="Preview"
                    className="h-full w-full"
                    allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div
                  className={cx(
                    "grid aspect-video place-items-center rounded-xl border border-dashed",
                    "border-ink-200 text-center text-xs text-ink-400",
                  )}
                >
                  Paste a video link to preview it
                </div>
              )}
            </Card>

            {isEdit && form.appId && env.PUBLIC_APP_URL && (
              <Card title="Public page">
                {/* Lives in the separate public app, so this is a real link. */}
                <Button
                  as="a"
                  href={`${env.PUBLIC_APP_URL}/apps/${encodeURIComponent(form.appId)}/get-started`}
                  target="_blank"
                  rel="noreferrer"
                  variant="secondary"
                  size="sm"
                  className="w-full"
                >
                  Open get started page
                </Button>
              </Card>
            )}
          </aside>
        </div>
      )}
    </form>
  );
};

export default AppForm;
